import React, {
  FC,
  createContext,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  ApolloError,
  useMutation,
  useLazyQuery,
  FetchResult,
} from "@apollo/client";
import { useLocation } from "react-router-dom";

import SignUpService from "@app/service/signUp";
import UserService, { IDP_TYPE } from "@app/service/user";
import { User } from "@app/provider/user";
import { getGraphQLErrorCode } from "@app/provider/error/graphQLErrorCode";
import { Language } from "@app/provider";

interface SignUpData {
  email: string;
  password: string;
}

interface VerifyEmailCodeProps extends SignUpData {
  language: Language;
  code: string;
  token?: string;
  // UPP-5864: DOB-proof handle (required with a link token on the OTP path).
  intentToken?: string;
  recaptchaToken?: string;
  recaptchaV2Token?: string;
}

interface SendActivationCodeProps {
  email: string;
  language: Language;
  recaptchaToken?: string;
  recaptchaV2Token?: string;
}

export interface SignUpByPasswordProps {
  email?: string;
  password: string;
  language: Language;
  token: string | null;
  intentToken?: string;
  recaptchaToken?: string;
  recaptchaV2Token?: string;
}

export enum VerificationMessageRes {
  SUCCESS = "SUCCESS",
  VERIFIED = "VERIFIED",
  EXPIRED = "EXPIRED",
  INVALID = "INVALID",
  ERROR = "ERROR",
}

interface VerifyEmailCodeResponse {
  success: boolean;
  message: VerificationMessageRes;
}

interface CheckUserExistedResponse {
  isExisted: boolean;
  idpType?: IDP_TYPE;
}

interface SignUpResponse {
  isTokenValid?: boolean;
  user?: User;
}

export interface SignUpController {
  signUpByPasswordIsLoading: boolean;
  checkUserExistedIsLoading: boolean;
  signUpByPasswordData?: SignUpResponse;
  checkUserExistedData?: CheckUserExistedResponse;
  signUpByPasswordError?: string;
  signUpByPasswordNetworkError?: string;
  checkUserExitedError?: ApolloError;
  signUpByPassword: (
    props: SignUpByPasswordProps
  ) => Promise<FetchResult<{ signUpByPassword: SignUpResponse }>>;
  checkUserExisted: (
    email: string
  ) => Promise<CheckUserExistedResponse | undefined>;
  signUpData?: SignUpData | null;
  setSignUpData: (data: SignUpData | null) => void;
  verifyEmailCodeIsLoading: boolean;
  verifyEmailCodeData?: VerifyEmailCodeResponse;
  verifyEmailCodeError?: string;
  verifyEmailCode: (
    props: VerifyEmailCodeProps
  ) => Promise<FetchResult<{ verifyEmailCode: VerifyEmailCodeResponse }>>;
  sendActivationCodeIsLoading: boolean;
  sendActivationCodeData?: boolean;
  sendActivationCodeError?: string;
  sendActivationCode: (
    props: SendActivationCodeProps
  ) => Promise<FetchResult<{ sendActivationCode: boolean }>>;
}

export const Context = createContext<SignUpController>({
  signUpByPasswordIsLoading: false,
  checkUserExistedIsLoading: false,
  signUpByPassword: async () => ({}),
  checkUserExisted: async () => undefined,
  setSignUpData: () => {},
  verifyEmailCodeIsLoading: false,
  sendActivationCodeIsLoading: false,
  verifyEmailCode: async () => ({}),
  sendActivationCode: async () => ({}),
});

Context.displayName = "SignUpContext";

const SignUpProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [signUpData, setSignUpData] = useState<SignUpData | null>(null);
  const location = useLocation();

  useEffect(() => {
    if (!signUpData) return;

    // Lazy import to avoid circular dependency with @app/routing
    const { routes } = require("@app/routing");
    const signUpFlowRoutes = [routes.signUp, routes.activation];
    const isSignUpFlow = signUpFlowRoutes.some(
      (route: string) =>
        location.pathname === route || location.pathname.startsWith(`${route}/`)
    );

    if (!isSignUpFlow) {
      setSignUpData(null);
    }
  }, [location.pathname]);

  const [
    signUpByPassword,
    {
      loading: signUpByPasswordIsLoading,
      data: signUpByPasswordData,
      error: signUpByPasswordError,
    },
  ] = useMutation<{
    signUpByPassword: SignUpResponse;
  }>(SignUpService.signUpByPassword());

  const [
    verifyEmailCode,
    {
      loading: verifyEmailCodeIsLoading,
      data: verifyEmailCodeData,
      error: verifyEmailCodeError,
    },
  ] = useMutation<{
    verifyEmailCode: VerifyEmailCodeResponse;
  }>(SignUpService.verifyEmailCode());

  const [
    sendActivationCode,
    {
      loading: sendActivationCodeIsLoading,
      data: sendActivationCodeData,
      error: sendActivationCodeError,
    },
  ] = useMutation<{
    sendActivationCode: boolean;
  }>(SignUpService.sendActivationCode());

  const [
    checkUserExisted,
    {
      loading: checkUserExistedIsLoading,
      data: checkUserExistedData,
      error: checkUserExitedError,
    },
  ] = useLazyQuery<{ checkUserExisted: CheckUserExistedResponse }>(
    UserService.CheckUserExisted(),
    { fetchPolicy: "no-cache" }
  );

  const handleSignUpByPassword = async (props: SignUpByPasswordProps) =>
    signUpByPassword({
      variables: {
        signUpByPasswordData: props,
      },
    });

  const handleCheckUserExisted = async (email: string) => {
    const user = await checkUserExisted({
      variables: { email },
    });
    return user?.data?.checkUserExisted;
  };

  const handleVerifyEmailCode = async (props: VerifyEmailCodeProps) =>
    verifyEmailCode({
      variables: {
        verifyEmailCodeData: props,
      },
    });

  const handleSendActivationCode = async (props: SendActivationCodeProps) =>
    sendActivationCode({
      variables: {
        sendActivationCodeData: props,
      },
    });

  const contextValue = useMemo(
    () => ({
      signUpData,
      setSignUpData,
      signUpByPasswordIsLoading,
      checkUserExistedIsLoading,
      signUpByPasswordData: signUpByPasswordData?.signUpByPassword,
      checkUserExistedData: checkUserExistedData?.checkUserExisted,
      signUpByPasswordError: getGraphQLErrorCode(signUpByPasswordError),
      signUpByPasswordNetworkError: signUpByPasswordError?.networkError?.name,
      checkUserExitedError,
      signUpByPassword: handleSignUpByPassword,
      checkUserExisted: handleCheckUserExisted,
      verifyEmailCodeIsLoading,
      verifyEmailCodeData: verifyEmailCodeData?.verifyEmailCode,
      verifyEmailCodeError: getGraphQLErrorCode(verifyEmailCodeError),
      verifyEmailCode: handleVerifyEmailCode,
      sendActivationCodeIsLoading,
      sendActivationCodeData: sendActivationCodeData?.sendActivationCode,
      sendActivationCodeError: getGraphQLErrorCode(sendActivationCodeError),
      sendActivationCode: handleSendActivationCode,
    }),
    [
      signUpData,
      signUpByPasswordIsLoading,
      checkUserExistedIsLoading,
      verifyEmailCodeIsLoading,
      sendActivationCodeIsLoading,
      signUpByPasswordData,
      checkUserExistedData,
      verifyEmailCodeData,
      sendActivationCodeData,
      signUpByPasswordError,
      checkUserExitedError,
      handleSignUpByPassword,
      handleCheckUserExisted,
      handleVerifyEmailCode,
      handleSendActivationCode,
    ]
  );

  return <Context.Provider value={contextValue}>{children}</Context.Provider>;
};

export default SignUpProvider;
